import { AlertBannerType, ExAlertBanner } from '@boomi/exosphere';
import { useEffect, useMemo, useState } from 'react';
import '../../../css/metadataEditor.css';
import { GRAPH_ELEMENT_TYPES } from '../constants/graph';
import {
    type GroupElementResponseAPI,
    type MapElementResponseAPI,
    getGroupElement,
    getMapElement,
    saveGroupElement,
    saveMapElement,
} from '../sources/graph';
import translations from '../translations';
import { stringReplace } from '../utils/string';
import ButtonDefault from './buttons/ButtonDefault';
import ButtonPrimary from './buttons/ButtonPrimary';
import CodeEditor from './generic/CodeEditor';
import Modal from './generic/modal/GenericModal';
import Loader from './loader/Loader';
import { useNotificationDispatch } from './notifications/NotificationProvider';

type MetadataEditorProps = {
    id: string;
    flowId: string;
    isVisible: boolean;
    elementType: string | null | undefined;
    container: HTMLElement | null | undefined;
    toggleMetadata: () => void;
};

const MetadataEditor = ({
    id,
    flowId,
    isVisible,
    elementType,
    container,
    toggleMetadata,
}: MetadataEditorProps) => {
    const [metadata, setMetadata] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const elementTypeLower = useMemo(() => elementType?.toLowerCase(), [elementType]);
    const dispatchNotification = useNotificationDispatch();

    useEffect(() => {
        const loadMetadata = async () => {
            try {
                setIsLoading(true);

                let response: MapElementResponseAPI | GroupElementResponseAPI;

                switch (elementTypeLower) {
                    case GRAPH_ELEMENT_TYPES.map: {
                        response = await getMapElement(id, flowId);
                        break;
                    }
                    case GRAPH_ELEMENT_TYPES.group: {
                        response = await getGroupElement(id, flowId);
                        break;
                    }
                    default: {
                        dispatchNotification({
                            type: 'notifyError',
                            payload: {
                                error: stringReplace(
                                    translations.GRAPH_metadata_editor_unsupported_element_type_error_message,
                                    { type: elementTypeLower },
                                ),
                            },
                        });

                        return;
                    }
                }

                setMetadata(JSON.stringify(response, null, 4));
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            } finally {
                setIsLoading(false);
            }
        };

        if (!isVisible) {
            return;
        }

        loadMetadata();
    }, [flowId, id, isVisible, dispatchNotification, elementTypeLower]);

    const handleChange = (value: string) => {
        setMetadata(value);
    };

    const handleHide = () => {
        toggleMetadata();
    };

    const handleSave = async () => {
        try {
            setIsLoading(true);

            const json = JSON.parse(metadata);

            switch (elementTypeLower) {
                case GRAPH_ELEMENT_TYPES.map: {
                    await saveMapElement(json, flowId);
                    break;
                }
                case GRAPH_ELEMENT_TYPES.group: {
                    await saveGroupElement(json, flowId);
                    break;
                }
                default: {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: {
                            error: stringReplace(
                                translations.GRAPH_metadata_editor_unsupported_element_type_error_message,
                                { type: elementTypeLower },
                            ),
                        },
                    });
                    return;
                }
            }

            dispatchNotification({
                type: 'notifySuccess',
                payload: {
                    message: translations.GRAPH_metadata_editor_save_success_message,
                },
            });
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            show={isVisible}
            onHide={handleHide}
            container={container}
            className="metadata-editor config-modal"
            title={translations.GRAPH_metadata_editor_title}
            renderBody={() => (
                <>
                    <CodeEditor
                        value={metadata}
                        name={`metadata-editor-${flowId}`}
                        onChange={handleChange}
                    />
                    <ExAlertBanner type={AlertBannerType.WARNING} open>
                        {translations.GRAPH_metadata_editor_saving_metadata_alert_text}
                    </ExAlertBanner>
                    {isLoading && <Loader />}
                </>
            )}
            renderFooter={() => (
                <>
                    <ButtonDefault onClick={handleHide}>{translations.COMMON_close}</ButtonDefault>
                    <ButtonPrimary onClick={handleSave}>{translations.COMMON_save}</ButtonPrimary>
                </>
            )}
        />
    );
};

export default MetadataEditor;
